import logger from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const socialIcons = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  email: Mail
};

export default function Footer() {
  const { language } = useLanguage();
  const [siteConfig, setSiteConfig] = useState(null);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [configRes, pagesRes] = await Promise.all([
        fetch(`${API_URL}/api/public/site-config`),
        fetch(`${API_URL}/api/public/pages`)
      ]);
      
      if (configRes.ok) {
        const config = await configRes.json();
        setSiteConfig(config);
      }
      
      if (pagesRes.ok) {
        const data = await pagesRes.json();
        setPages(data.pages || []);
      }
    } catch (error) {
      logger.error('Error fetching footer data:', error);
    }
  };

  const footerPages = pages.filter(p => p.show_in_footer);
  const legalPages = footerPages.filter(p => p.is_legal);
  const otherPages = footerPages.filter(p => !p.is_legal);

  const copyright = siteConfig?.footer_copyright?.[language] || `© ${new Date().getFullYear()} PayHub. ${language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}`;
  const siteName = siteConfig?.site_name?.[language] || 'PayHub';

  return (
    <footer className="bg-gray-900 text-gray-300 mt-auto">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo & Description */}
          <div className="md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <div className="bg-white rounded-lg p-2">
                <img 
                  src="/quebec-plus-logo.png" 
                  alt="Québec+" 
                  className="h-10 object-contain"
                />
              </div>
            </Link>
            <p className="text-sm text-gray-400">
              {siteConfig?.tagline?.[language] || (language === 'fr' ? 'Votre portefeuille numérique' : 'Your digital wallet')}
            </p>
            
            {/* Social Links */}
            {siteConfig?.social_links && Object.keys(siteConfig.social_links).length > 0 && (
              <div className="flex gap-3 mt-4">
                {Object.entries(siteConfig.social_links).map(([key, url]) => {
                  if (!url) return null;
                  const Icon = socialIcons[key] || Mail;
                  return (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="h-9 w-9 rounded-full bg-gray-800 hover:bg-primary transition-colors flex items-center justify-center"
                    >
                      <Icon className="h-4 w-4" />
                    </a>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {language === 'fr' ? 'Liens rapides' : 'Quick Links'}
            </h3>
            <ul className="space-y-2 text-sm">
              <li><Link to="/rejoignez-nous" className="hover:text-white transition-colors">{language === 'fr' ? 'Rejoignez-nous' : 'Join Us'}</Link></li>
              <li><Link to="/dashboard" className="hover:text-white transition-colors">{language === 'fr' ? 'Tableau de bord' : 'Dashboard'}</Link></li>
              <li><Link to="/topup" className="hover:text-white transition-colors">{language === 'fr' ? 'Recharger' : 'Top Up'}</Link></li>
              <li><Link to="/transfer" className="hover:text-white transition-colors">{language === 'fr' ? 'Transférer' : 'Transfer'}</Link></li>
              <li><Link to="/transactions" className="hover:text-white transition-colors">{language === 'fr' ? 'Transactions' : 'Transactions'}</Link></li>
              {otherPages.map(page => (
                <li key={page.page_id}>
                  <Link to={`/page/${page.slug}`} className="hover:text-white transition-colors">
                    {page.title[language] || page.title.en || page.title.fr}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {language === 'fr' ? 'Légal' : 'Legal'}
            </h3>
            <ul className="space-y-2 text-sm">
              {legalPages.map(page => (
                <li key={page.page_id}>
                  <Link to={`/page/${page.slug}`} className="hover:text-white transition-colors">
                    {page.title[language] || page.title.en || page.title.fr}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-white mb-4">
              {language === 'fr' ? 'Contact' : 'Contact'}
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                <a href="mailto:support@payhub.quebec.plus" className="hover:text-white transition-colors">
                  support@payhub.quebec.plus
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
          <p>© 2026 PayHub.Québec<sup>+</sup> {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}</p>
        </div>
      </div>
    </footer>
  );
}
